// pages/CompanyAddresses/CompanyAddresses.js — Updated with dynamic city counts in dropdown
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FaBuilding, FaSearch, FaPlus, FaTrash, FaEdit, FaCheckDouble,
  FaEllipsisV, FaMapMarkerAlt, FaStore, FaChevronDown, FaTimes
} from 'react-icons/fa';
import { RiDeleteBin5Fill } from "react-icons/ri";
import { IoMdArrowDropdown } from "react-icons/io";
import Swal from 'sweetalert2';
import axiosInstance from '../../utils/axiosInstance';
import './CompanyAddresses.css';

// Last loaded addresses of each shop are kept, so the list shows instantly
// on the next visit while fresh data loads in the background.
const CA_CACHE_PREFIX = 'companyaddr_cache_';
const CA_CITIES_PREFIX = 'companyaddr_cities_';
const CA_SHOPS_KEY = 'companyaddr_shops_cache';

const readJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {}
};

const getShopIdFromPath = (pathname) => {
  const match = pathname.match(/\/shop\/(\d+)/);
  return match ? parseInt(match[1]) : null;
};

const CompanyAddresses = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // currentShopId from URL (known from the very first render)
  const currentShopId = useMemo(() => getShopIdFromPath(location.pathname), [location.pathname]);

  // The saved list of the shop is shown instantly while fresh data loads in the background.
  const initialRef = useRef(null);
  if (initialRef.current === null) {
    const key = currentShopId || 'all';
    const cachedAddresses = readJson(`${CA_CACHE_PREFIX}${key}`);
    const cachedCities = currentShopId ? readJson(`${CA_CITIES_PREFIX}${currentShopId}`) : null;
    const cachedShops = readJson(CA_SHOPS_KEY);
    initialRef.current = {
      addresses: Array.isArray(cachedAddresses) ? cachedAddresses : null,
      cities: Array.isArray(cachedCities) ? cachedCities : null,
      shops: Array.isArray(cachedShops) ? cachedShops : null
    };
  }

  // State
  const [addressesList, setAddressesList] = useState(initialRef.current.addresses || []);
  const [masterAddressesList, setMasterAddressesList] = useState(initialRef.current.addresses || []); // ✅ Keeps all addresses for accurate counting
  const [shops, setShops] = useState(initialRef.current.shops || []);
  const [selectedAddresses, setSelectedAddresses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showActionDropdown, setShowActionDropdown] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  // True only while there is nothing to show yet (so "No addresses found" never flashes)
  const [isLoading, setIsLoading] = useState(!initialRef.current.addresses);
  const [cities, setCities] = useState(initialRef.current.cities || []);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const cityInputRef = useRef(null);
  const cityDropdownRef = useRef(null);

  // ✅ City filter dropdown (header)
  const [filterCities, setFilterCities] = useState(initialRef.current.cities || []);
  const [selectedFilterCity, setSelectedFilterCity] = useState('');

  const selectedCityRef = useRef('');
  const allRequestRef = useRef(0);
  const cityRequestRef = useRef(0);
  const filterCitiesRequestRef = useRef(0);
  const modalCitiesRequestRef = useRef(0);
  const prevShopKeyRef = useRef(currentShopId || 'all');

  const [formData, setFormData] = useState({
    city: '',
    area: '',
    street_Number: '',
    postal_code: '',
    shop_id: '',
    is_default: false
  });

  // ✅ fetchAddresses accepts an optional city filter (runs in the background:
  // only the latest request of each kind is allowed to update the screen)
  // cityFilter = null  -> all addresses (master list, also saved for next time)
  // cityFilter = 'xyz' -> addresses of that city (backend filter)
  const fetchAddresses = useCallback(async (cityFilter = null) => {
    const isCity = !!(cityFilter && cityFilter !== '' && cityFilter !== 'all');
    const requestRef = isCity ? cityRequestRef : allRequestRef;
    const requestId = ++requestRef.current;
    const shopKey = currentShopId || 'all';

    try {
      let url = '/user/company-addresses/';
      const params = new URLSearchParams();
      if (currentShopId) {
        params.append('shop_id', currentShopId);
      }
      if (isCity) {
        params.append('city', cityFilter);
      }
      const qs = params.toString();
      if (qs) url += `?${qs}`;

      console.log('🔄 Fetching addresses:', url);
      const response = await axiosInstance.get(url);
      if (requestId !== requestRef.current) return;
      console.log('✅ Addresses response:', response.data.length, 'addresses');

      if (!isCity) {
        // ✅ Master list (all cities) - used to calculate accurate global counts
        setMasterAddressesList(response.data);
        writeJson(`${CA_CACHE_PREFIX}${shopKey}`, response.data);
        const activeCity = selectedCityRef.current;
        setAddressesList(
          activeCity ? response.data.filter(address => address.city === activeCity) : response.data
        );
      } else if (cityFilter === selectedCityRef.current) {
        setAddressesList(response.data);
      }

    } catch (err) {
      if (requestId !== requestRef.current) return;
      console.error('Error fetching addresses:', err);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load addresses',
        timer: 3000,
        showConfirmButton: true
      });
    } finally {
      if (!isCity && requestId === requestRef.current) setIsLoading(false);
    }
  }, [currentShopId]);

  const fetchShops = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/user/shops/');
      setShops(response.data);
      writeJson(CA_SHOPS_KEY, response.data);
    } catch (err) {
      console.error('Error fetching shops:', err);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load shops',
        timer: 3000,
        showConfirmButton: true
      });
    }
  }, []);

  // Shops list is loaded once
  useEffect(() => {
    fetchShops();
  }, [fetchShops]);

  // Data of the shop: show the saved list immediately, then refresh it.
  // Addresses and cities load together instead of one after another.
  useEffect(() => {
    const key = currentShopId || 'all';

    if (prevShopKeyRef.current !== key) {
      prevShopKeyRef.current = key;
      const cachedAddresses = readJson(`${CA_CACHE_PREFIX}${key}`);
      const cachedCities = currentShopId ? readJson(`${CA_CITIES_PREFIX}${currentShopId}`) : null;
      if (Array.isArray(cachedAddresses)) {
        setAddressesList(cachedAddresses);
        setMasterAddressesList(cachedAddresses);
        setIsLoading(false);
      } else {
        setAddressesList([]);
        setMasterAddressesList([]);
        setIsLoading(true);
      }
      const savedCities = Array.isArray(cachedCities) ? cachedCities : [];
      setFilterCities(savedCities);
      setCities(savedCities);
      setSelectedFilterCity('');
      selectedCityRef.current = '';
      setSelectedAddresses([]);
    }

    fetchAddresses(null);

    if (currentShopId) {
      fetchFilterCities(currentShopId);
    } else {
      setFilterCities([]);
      setSelectedFilterCity('');
      selectedCityRef.current = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentShopId, fetchAddresses]);

  const fetchFilterCities = async (shopId) => {
    if (!shopId) return;
    const requestId = ++filterCitiesRequestRef.current;
    try {
      const response = await axiosInstance.get(`/user/shop-cities/?shop_id=${shopId}`);
      if (requestId !== filterCitiesRequestRef.current) return;
      let citiesData = [];
      if (response.data) {
        if (Array.isArray(response.data.cities)) {
          citiesData = response.data.cities;
        } else if (Array.isArray(response.data)) {
          citiesData = response.data;
        } else if (typeof response.data.cities === 'string') {
          try {
            citiesData = JSON.parse(response.data.cities);
          } catch {
            citiesData = response.data.cities.split(',').map(c => c.trim()).filter(c => c);
          }
        }
      }
      setFilterCities(citiesData);
      writeJson(`${CA_CITIES_PREFIX}${shopId}`, citiesData);
    } catch (err) {
      if (requestId !== filterCitiesRequestRef.current) return;
      console.error('Error fetching shop cities:', err);
    }
  };

  // City change: the list is filtered instantly from the loaded addresses,
  // and refreshed from the server in the background.
  const applyCity = (newCity) => {
    setSelectedFilterCity(newCity);
    selectedCityRef.current = newCity;
    setSelectedAddresses([]);
    setAddressesList(
      newCity ? masterAddressesList.filter(address => address.city === newCity) : masterAddressesList
    );
    if (newCity) fetchAddresses(newCity);
  };

  const handleFilterCityChange = (e) => {
    applyCity(e.target.value);
  };

  const fetchShopCities = async (shopId) => {
    if (!shopId) {
      setCities([]);
      return;
    }
    const requestId = ++modalCitiesRequestRef.current;
    try {
      const response = await axiosInstance.get(`/user/shop-cities/?shop_id=${shopId}`);
      if (requestId !== modalCitiesRequestRef.current) return;
      if (response.data && response.data.cities) {
        const citiesData = Array.isArray(response.data.cities) ? response.data.cities : [];
        setCities(citiesData);
        writeJson(`${CA_CITIES_PREFIX}${shopId}`, citiesData);
      } else {
        setCities([]);
      }
    } catch (err) {
      if (requestId !== modalCitiesRequestRef.current) return;
      console.error('Error fetching shop cities:', err);
    }
  };

  useEffect(() => {
    const shopId = formData.shop_id || currentShopId;
    if (shopId) {
      fetchShopCities(shopId);
    } else {
      setCities([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.shop_id, currentShopId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target) &&
          cityInputRef.current && !cityInputRef.current.contains(event.target)) {
        setShowCityDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filteredAddresses = addressesList.filter(address =>
    address.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    address.area?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    address.street_Number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    address.postal_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    address.shop_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleAddressSelection = (address) => {
    setSelectedAddresses(prev => {
      const isSelected = prev.some(a => a.address_Id === address.address_Id);
      if (isSelected) {
        return prev.filter(a => a.address_Id !== address.address_Id);
      } else {
        return [...prev, address];
      }
    });
    setShowActionDropdown(false);
  };

  // Deleted addresses disappear right away (and from the saved copy too)
  const removeAddressesLocally = (ids) => {
    const remove = (list) => list.filter(a => !ids.includes(a.address_Id));
    setAddressesList(prev => remove(prev));
    setMasterAddressesList(prev => {
      const next = remove(prev);
      writeJson(`${CA_CACHE_PREFIX}${currentShopId || 'all'}`, next);
      return next;
    });
  };

  const handleDeleteSelected = async () => {
    if (selectedAddresses.length === 0) {
      await Swal.fire({
        icon: 'warning',
        title: 'No Selection',
        text: 'Please select addresses to delete',
        timer: 2000,
        showConfirmButton: true
      });
      return;
    }
    const result = await Swal.fire({
      title: 'Confirm Deletion',
      text: `Are you sure you want to delete ${selectedAddresses.length} selected address(es)?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete them!',
      cancelButtonText: 'Cancel'
    });
    if (result.isConfirmed) {
      try {
        const deletedCount = selectedAddresses.length;
        const deletedIds = [];
        for (const address of selectedAddresses) {
          await axiosInstance.delete(`/user/company-addresses/${address.address_Id}/`);
          deletedIds.push(address.address_Id);
        }
        removeAddressesLocally(deletedIds);
        fetchAddresses(null);
        setSelectedAddresses([]);
        setShowActionDropdown(false);
        await Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: `${deletedCount} address(es) deleted successfully!`,
          timer: 2000,
          showConfirmButton: true
        });
      } catch (err) {
        console.error('Error deleting addresses:', err);
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete addresses',
          timer: 3000,
          showConfirmButton: true
        });
      }
    }
  };

  const handleDeleteSingle = async (address) => {
    const result = await Swal.fire({
      title: 'Delete Address?',
      html: `Are you sure you want to delete <strong></strong>?<br/><small style="color:#dc3545;"></small>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      reverseButtons: true
    });
    if (result.isConfirmed) {
      try {
        await axiosInstance.delete(`/user/company-addresses/${address.address_Id}/`);
        removeAddressesLocally([address.address_Id]);
        fetchAddresses(null);
        setSelectedAddresses(prev =>
          prev.filter(a => a.address_Id !== address.address_Id)
        );
        await Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Address has been deleted successfully!',
          timer: 2000,
          showConfirmButton: false
        });
      } catch (err) {
        console.error('Error deleting address:', err);
        await Swal.fire({
          icon: 'error',
          title: 'Delete Failed',
          text: 'Failed to delete the address. Please try again.',
          confirmButtonColor: '#004aad'
        });
      }
    }
  };

  const handleSelectAll = () => {
    if (selectedAddresses.length === filteredAddresses.length) {
      setSelectedAddresses([]);
    } else {
      setSelectedAddresses([...filteredAddresses]);
    }
    setShowActionDropdown(false);
  };

  const handleAddAddress = () => {
    setEditingAddress(null);
    setFormData({
      city: '',
      area: '',
      street_Number: '',
      postal_code: '',
      shop_id: currentShopId || '',
      is_default: false
    });
    setShowModal(true);
    setShowCityDropdown(false);
  };

  const handleEditAddress = (address) => {
    setEditingAddress(address);
    setFormData({
      city: address.city || '',
      area: address.area || '',
      street_Number: address.street_Number || '',
      postal_code: address.postal_code || '',
      shop_id: address.shop || currentShopId || '',
      is_default: address.is_default || false
    });
    setShowModal(true);
    setShowCityDropdown(false);
    const shopId = address.shop || currentShopId;
    if (shopId) {
      fetchShopCities(shopId);
    }
  };

  const handleSaveAddress = async () => {
    if (!formData.city && !formData.area && !formData.street_Number && !formData.postal_code) {
      await Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Please fill in at least one address field',
        timer: 2000,
        showConfirmButton: true
      });
      return;
    }
    if (!formData.shop_id && !currentShopId) {
      await Swal.fire({
        icon: 'warning',
        title: 'Shop Required',
        text: 'Please select a shop',
        timer: 2000,
        showConfirmButton: true
      });
      return;
    }
    try {
      const dataToSend = {
        city: formData.city,
        area: formData.area,
        street_Number: formData.street_Number,
        postal_code: formData.postal_code,
        shop: parseInt(formData.shop_id || currentShopId),
        is_default: formData.is_default
      };
      if (editingAddress) {
        await axiosInstance.put(`/user/company-addresses/${editingAddress.address_Id}/`, dataToSend);
        // the list is refreshed behind the alert
        fetchAddresses(null);
        await Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Address updated successfully!',
          timer: 2000,
          showConfirmButton: true
        });
      } else {
        await axiosInstance.post('/user/company-addresses/', dataToSend);
        fetchAddresses(null);
        await Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Address added successfully!',
          timer: 2000,
          showConfirmButton: true
        });
      }
      setShowModal(false);
      setEditingAddress(null);
    } catch (err) {
      console.error('Error saving address:', err);
      let errorMessage = 'Failed to save address';
      if (err.response) {
        errorMessage = `Failed to save address: ${err.response.data.error || 'Please check your data'}`;
      }
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage,
        timer: 3000,
        showConfirmButton: true
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleCityInputChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, city: value });
    setShowCityDropdown(true);
  };

  const handleCitySelect = (city) => {
    setFormData({ ...formData, city: city });
    setShowCityDropdown(false);
  };

  const toggleCityDropdown = () => {
    setShowCityDropdown(!showCityDropdown);
  };

  const clearCity = () => {
    setFormData({ ...formData, city: '' });
    setShowCityDropdown(false);
    if (cityInputRef.current) {
      cityInputRef.current.focus();
    }
  };

  const filteredCities = cities.filter(city =>
    city.toLowerCase().includes(formData.city.toLowerCase())
  );

  const handleClickOutside = (e) => {
    if (showActionDropdown && !e.target.closest('.action-dropdown-container')) {
      setShowActionDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showActionDropdown]);

  const currentShop = shops.find(s => s.shop_id === currentShopId);

  const getShopName = (shopId) => {
    const shop = shops.find(s => s.shop_id === parseInt(shopId));
    return shop ? shop.shop_name : 'Unknown Shop';
  };

  // ✅ Calculate total addresses for "All Cities" (using master list)
  const allCitiesCount = masterAddressesList.length;

  // ✅ Helper to calculate count for a specific city (using master list)
  const getCityCount = (cityName) => {
    return masterAddressesList.filter(address => address.city === cityName).length;
  };

  return (
    <div className="company-addresses-container">
      {/* Header Section */}
      <div className="addresses-header-section">
        <div className="addresses-header-left">
          {/* ✅ FaBuilding Icon added */}
          <FaBuilding className="addresses-header-icon" />
          <h2 className="addresses-header-title">Company Addresses</h2>

          {/* ✅ Dynamic City Badge with Count */}
          <span className="shop-city-badge">
            <FaMapMarkerAlt className="shop-city-icon" />
            <span className="shop-city-name">
              {selectedFilterCity || 'All Cities'} ({selectedFilterCity ? getCityCount(selectedFilterCity) : allCitiesCount})
            </span>
          </span>
        </div>

        <div className="addresses-header-right">
          {/* ✅ City filter dropdown with custom arrow and dynamic counts */}
          <div className="city-dropdown-wrapper">
            <select
              className="city-select-dropdown"
              value={selectedFilterCity}
              onChange={handleFilterCityChange}
              disabled={filterCities.length === 0}
            >
              {/* ✅ Dynamic "All Cities" option showing total count */}
              <option value="">
                All Cities ({allCitiesCount})
              </option>

              {/* ✅ Dynamic city options showing their respective counts */}
              {filterCities.map((city, index) => (
                <option key={index} value={city}>
                  {city} ({getCityCount(city)})
                </option>
              ))}
            </select>
            <IoMdArrowDropdown className="city-dropdown-icon" />
          </div>

          <div className="search-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search addresses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <button className="select-all-btn" onClick={handleSelectAll}>
            <FaCheckDouble />
            <span>{selectedAddresses.length === filteredAddresses.length && filteredAddresses.length > 0 ? 'Deselect All' : 'Select All'}</span>
          </button>
          <button className="add-address-btn" onClick={handleAddAddress}>
            <FaPlus />
            <span>ADD ADDRESS</span>
          </button>
        </div>
      </div>

      <div className="addresses-content">
        {selectedAddresses.length > 0 && (
          <div className="selected-summary">
            <div className="summary-content">
              <span className="summary-icon">✓</span>
              <span className="summary-text">
                {selectedAddresses.length} address(es) selected
              </span>
              <div className="action-dropdown-container">
                <button
                  className="action-dropdown-btn"
                  onClick={() => setShowActionDropdown(!showActionDropdown)}
                >
                  <FaEllipsisV />
                  <span>Actions</span>
                </button>
                {showActionDropdown && (
                  <div className="action-dropdown-menu">
                    <button
                      className="dropdown-item delete-item"
                      onClick={handleDeleteSelected}
                    >
                      <FaTrash />
                      <span>Delete Selected ({selectedAddresses.length})</span>
                    </button>
                  </div>
                )}
              </div>
              <button className="clear-selection" onClick={() => {
                setSelectedAddresses([]);
                setShowActionDropdown(false);
              }}>
                Clear all
              </button>
            </div>
          </div>
        )}

        <div className="addresses-table-container">
          <div className="addresses-table-wrapper">
            <table className="addresses-table">
              <thead>
                <tr>
                  <th style={{ width: '50px' }}>
                    <input
                      type="checkbox"
                      checked={selectedAddresses.length === filteredAddresses.length && filteredAddresses.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>City</th>
                  <th>Area</th>
                  <th>Street Number</th>
                  <th>Postcode</th>
                  {!currentShopId && <th>Shop</th>}
                  <th>Default</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* Nothing is shown until the first load is done, so no wrong / empty list ever flashes */}
                {isLoading ? (
                  <tr>
                    <td colSpan={currentShopId ? 7 : 8} style={{ height: '200px' }}></td>
                  </tr>
                ) : (
                  filteredAddresses.map((address) => (
                    <tr key={address.address_Id} className={selectedAddresses.some(a => a.address_Id === address.address_Id) ? 'selected' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedAddresses.some(a => a.address_Id === address.address_Id)}
                          onChange={() => toggleAddressSelection(address)}
                        />
                      </td>
                      <td>{address.city || 'N/A'}</td>
                      <td>{address.area || 'N/A'}</td>
                      <td className="street-number-cell">
                        <div className="street-number-content">
                          <span>{address.street_Number || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="postcode-cell">
                        {address.postal_code || 'N/A'}
                      </td>
                      {!currentShopId && (
                        <td>
                          <span className="shop-badge">
                            {address.shop_name || 'N/A'}
                          </span>
                        </td>
                      )}
                      <td>
                        {address.is_default ? 'Default' : '-'}
                      </td>
                      <td className="actions-cell">
                        <div className="actions-cell-inner">
                          <button
                            className="edit-address-btn"
                            onClick={() => handleEditAddress(address)}
                          >
                            <FaEdit />
                          </button>

                          <button
                            className="delete-address-btn"
                            onClick={() => handleDeleteSingle(address)}
                          >
                            <RiDeleteBin5Fill />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {!isLoading && filteredAddresses.length === 0 && (
            <div className="no-addresses">
              <FaBuilding className="no-data-icon" />
              <p>
                {selectedFilterCity
                  ? `No addresses found in ${selectedFilterCity}.`
                  : 'No addresses found.'}
              </p>
              {selectedFilterCity ? (
                <button
                  className="add-first-btn"
                  onClick={() => applyCity('')}
                >
                  Clear City Filter
                </button>
              ) : (
                <button className="add-first-btn" onClick={handleAddAddress}>
                  <FaPlus /> Add Your First Address
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal for Add/Edit Address */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingAddress ? 'Edit Company Address' : 'Add Company Address'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <form className="address-form">
                {/* Shop Field - Always show as an Input Box */}
                <div className="form-group">
                  <label>SHOP <span className="required">*</span></label>
                  <input
                    type="text"
                    className="shop-name-input"
                    value={
                      editingAddress
                        ? getShopName(formData.shop_id)
                        : (currentShopId ? currentShop?.shop_name : (shops.find(s => s.shop_id === parseInt(formData.shop_id))?.shop_name || ''))
                    }
                    readOnly
                    placeholder="Shop Name"
                  />
                </div>

                {editingAddress && (
                  <div className="form-group info-group">
                    <label>Shop</label>
                    <div className="shop-info">
                      <FaStore />
                      <span>{getShopName(formData.shop_id)}</span>
                    </div>
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label>City <span className="required"></span></label>
                    <div className="city-combobox-container" ref={cityDropdownRef}>
                      <div className="city-input-wrapper">
                        <input
                          ref={cityInputRef}
                          type="text"
                          placeholder="Type city name or select from dropdown"
                          value={formData.city}
                          onChange={handleCityInputChange}
                          onFocus={() => setShowCityDropdown(true)}
                          className="city-combobox-input"
                          autoComplete="off"
                        />
                        <div className="city-combobox-actions">
                          {formData.city && (
                            <button
                              type="button"
                              className="city-clear-btn"
                              onClick={clearCity}
                              title="Clear city"
                            >
                              <FaTimes />
                            </button>
                          )}
                          <button
                            type="button"
                            className="city-dropdown-toggle"
                            onClick={toggleCityDropdown}
                            title="Toggle city dropdown"
                          >
                            <FaChevronDown className={showCityDropdown ? 'rotate-up' : ''} />
                          </button>
                        </div>
                      </div>
                      {showCityDropdown && (
                        <div className="city-dropdown-list">
                          {filteredCities.length > 0 ? (
                            filteredCities.map((city, index) => (
                              <div
                                key={index}
                                className={`city-dropdown-item ${city === formData.city ? 'selected' : ''}`}
                                onClick={() => handleCitySelect(city)}
                              >
                                {city}
                              </div>
                            ))
                          ) : (
                            <div className="city-dropdown-empty">
                              {formData.city ? (
                                <span>Type "{formData.city}" to add custom city</span>
                              ) : (
                                <span>No cities found. Type to add custom city</span>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <p className="field-hint info-hint">
                      <FaMapMarkerAlt /> Type to search or select from dropdown.
                    </p>
                  </div>
                  <div className="form-group">
                    <label>Postcode <span className="required"></span></label>
                    <input
                      type="text"
                      name="postal_code"
                      value={formData.postal_code}
                      onChange={handleInputChange}
                      placeholder="Postcode"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Area/County <span className="required"></span></label>
                    <input
                      type="text"
                      name="area"
                      value={formData.area}
                      onChange={handleInputChange}
                      placeholder="Area or County"
                    />
                  </div>
                  <div className="form-group">
                    <label>Street Number <span className="required"></span></label>
                    <input
                      type="text"
                      name="street_Number"
                      value={formData.street_Number}
                      onChange={handleInputChange}
                      placeholder="Street Number"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group full-width">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="is_default"
                        checked={formData.is_default}
                        onChange={handleInputChange}
                      />
                      Set as default address for this shop
                    </label>
                  </div>
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowModal(false)}>
                Cancel
              </button>
              <button className="btn-save" onClick={handleSaveAddress}>
                {editingAddress ? 'Update Address' : 'Save Address'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyAddresses;